import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from 'styled-components'
import Card from '../components/education/Card'
import MyClass from '../components/profile/my-class/MyClass'
import MyProfile from '../components/profile/MyProfile'
import Security from '../components/profile/Security'
import LogoutModal from '../components/LogoutModal'
import { getUserProfile, UserProfile } from '../libs/profile-service'

interface Prop {
    setTitle: React.Dispatch<React.SetStateAction<string>>
}

const Wrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 30px;
    background-color: #ffffff;
    border-radius: 10px;
`

const ContentWrapper = styled.div`
    padding: 30px;
    min-height: calc(100vh - 320px);
    display: flex;
    flex-direction: column;
    gap: 30px;
`

const ContentBodyWrapper = styled.div`
    padding: 30px;
    min-height: calc(100vh - 320px);
    display: flex;
    flex-direction: column;
    gap: 30px;
`

const Header = styled.div`
    height: 80px;
    display: flex;
    align-items: center;
    padding-left: 30px;
    border-bottom: 1px solid #e5e6eb;
`

const ButtonWrapper = styled.div`
    display: flex;
    gap: 10px;
    border: 1px solid #e5e6eb;
    border-radius: 50px;
    width: fit-content;
    padding: 5px;
    background-color: #f2f3f5;
`

const TabButton = styled.label<{ active: boolean }>`
    padding: 10px 20px;
    border-radius: 50px;
    cursor: pointer;
    &:hover {
        cursor: pointer;
        background-color: #ffffff;
    }
    background-color: ${(props) => (props.active ? '#ffffff' : 'transparent')};
    color: ${(props) => (props.active ? '#1fa474' : '#000000')};
`

const LogOutButton = styled.label<{ active: boolean }>`
    padding: 10px 20px;
    border-radius: 50px;
    cursor: pointer;
    &:hover {
        cursor: pointer;
        background-color: #ffffff;
    }
    background-color: ${(props) => (props.active ? '#ffffff' : 'transparent')};
    color: ${(props) => (props.active ? '#1fa474' : '#a41f21')};
`

const LogoutButton = styled.button`
    padding: 10px 20px;
    border-radius: 50px;
    cursor: pointer;
    border: none;
    background-color: transparent;
    color: #1fa474;
    &:hover {
        cursor: pointer;
        background-color: #ffffff;
    }
`

const ErrorDisplay = styled.div`
    padding: 20px;
    text-align: center;
    color: #ff4444;
    font-size: 18px;
    background-color: #ffe5e5;
    border-radius: 8px;
    margin: 20px;
`

const RetryButton = styled.button`
    width: fit-content;
    padding: 12px 32px;
    border-radius: 50px;
    cursor: pointer;
    border: none;
    background-color: #1fa474;
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    margin: 20px;
    &:hover {
        background-color: #188c5f;
    }
`

export default function Profile({ setTitle }: Prop) {
    const navigate = useNavigate()

    const [activeTab, setActiveTab] = useState<
        'MyClass' | 'MyProfile' | 'Security' | 'Logout'
    >('MyClass')
    const [showLogoutModal, setShowLogoutModal] = useState(false)
    const [profileData, setProfileData] = useState<UserProfile | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setTitle('Profile')
        fetchProfileData()
    }, [])

    const fetchProfileData = async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await getUserProfile()
            setProfileData(data)
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to load profile'
            )
            console.error('Error loading profile:', err)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <Wrapper>
                <Header>Loading...</Header>
            </Wrapper>
        )
    }

    if (error) {
        return (
            <Wrapper>
                <Header>Error loading profile</Header>
                <ContentWrapper>
                    <ErrorDisplay>{error}</ErrorDisplay>
                    <RetryButton onClick={fetchProfileData}>Retry</RetryButton>
                </ContentWrapper>
            </Wrapper>
        )
    }

    function handleTabChange(
        tab: 'MyClass' | 'MyProfile' | 'Security' | 'Logout'
    ) {
        setActiveTab(tab)
    }

    function handleLogoutClick() {
        setShowLogoutModal(true)
    }

    function handleLogout() {
        // Clear any stored authentication tokens
        localStorage.removeItem('token')
        sessionStorage.clear()
        // Navigate to login page
        navigate('/login')
    }

    return (
        <Wrapper>
            <Header>
                {profileData
                    ? `${profileData.first_name} ${profileData.last_name}`
                    : '[User Name] | [School]'}
            </Header>
            <ContentWrapper>
                <ButtonWrapper>
                    <TabButton
                        onClick={() => handleTabChange('MyClass')}
                        active={activeTab === 'MyClass'}
                    >
                        My Classes
                    </TabButton>
                    <TabButton
                        onClick={() => handleTabChange('MyProfile')}
                        active={activeTab === 'MyProfile'}
                    >
                        My Profile
                    </TabButton>
                    <TabButton
                        onClick={() => handleTabChange('Security')}
                        active={activeTab === 'Security'}
                    >
                        Security
                    </TabButton>
                    <LogOutButton
                        active={activeTab === 'Logout'}
                        onClick={handleLogoutClick}
                    >
                        Logout
                    </LogOutButton>
                </ButtonWrapper>
                <ContentBodyWrapper>
                    {activeTab === 'MyClass' && (
                        <MyClass profileData={profileData} />
                    )}
                    {activeTab === 'MyProfile' && (
                        <MyProfile
                            profileData={profileData}
                            onProfileUpdate={setProfileData}
                        />
                    )}
                    {activeTab === 'Security' && (
                        <Security profileData={profileData} />
                    )}
                </ContentBodyWrapper>
            </ContentWrapper>
            <LogoutModal
                isOpen={showLogoutModal}
                onConfirm={handleLogout}
                onCancel={() => setShowLogoutModal(false)}
            />
        </Wrapper>
    )
}
